<template>
  <q-card>
    <q-card-section>
      <div class="text-h6">
        {{ editMode ? t('common.edit') : t('facebook.quick_conversion.title') }}
      </div>
      <div v-if="editMode && editConversion" class="text-caption text-grey-6">
        ID: {{ editConversion.id }} | {{ editConversion.event_name }}
      </div>
      <div v-else class="text-caption">
        {{ t('facebook.quick_conversion.description') }}
      </div>
    </q-card-section>

    <q-separator />

    <q-card-section>
      <q-form @submit.prevent="sendConversion" class="form-compact">
        <!-- Селекты пикселя и типа события компактно в одной строке -->
        <div class="row items-start q-col-gutter-sm q-mb-sm">
          <div class="col-12 col-md-6">
            <q-select
                v-model="form.pixelId"
                :options="pixelOptions"
                option-value="pixel_id"
                option-label="label"
                :label="t('facebook.quick_conversion.select_pixel')"
                dense
                filled
                map-options
                emit-value
                :loading="loadingPixels"
                :rules="[val => !!val || t('facebook.quick_conversion.notifications.select_pixel_required')]"
                popup-content-class="select-popup-compact"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    {{ loadingPixels ? t('common.loading') : t('facebook.quick_conversion.no_pixels') }}
                  </q-item-section>
                </q-item>
              </template>
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps" dense>
                  <q-item-section>
                    <q-item-label class="select-label-compact">
                      {{ scope.opt.label }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
          <div class="col-12 col-md-6">
            <q-select
                v-model="form.eventName"
                :options="eventOptions"
                option-value="name"
                option-label="name"
                :label="t('facebook.quick_conversion.select_event')"
                dense
                filled
                map-options
                emit-value
                :loading="loadingEvents"
                :rules="[val => !!val || t('facebook.quick_conversion.notifications.select_event_required')]"
                popup-content-class="select-popup-compact"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps" dense>
                  <q-item-section>
                    <q-item-label class="select-label-compact">
                      {{ scope.opt.name }}
                    </q-item-label>
                    <q-item-label caption class="select-caption-compact">
                      {{ t('facebook.quick_conversion.events.' + scope.opt.key) }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
        </div>

        <!-- Кастомное событие -->
        <div v-if="form.eventName === 'Custom'" class="q-mb-sm">
          <q-input
              v-model="form.fields.customEventName"
              :label="t('facebook.quick_conversion.custom_event_name')"
              dense
              filled
              :rules="[val => !!val || t('facebook.quick_conversion.notifications.enter_event_name_required')]"
          />
        </div>

        <!-- Основные поля (3 в ряд) -->
        <div class="row items-start q-col-gutter-sm q-mb-sm">
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.email"
                :label="t('facebook.quick_conversion.fields.email')"
                type="email"
                dense
                filled
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.phone"
                :label="t('facebook.quick_conversion.fields.phone')"
                dense
                filled
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.fullName"
                :label="t('facebook.quick_conversion.fields.full_name')"
                dense
                filled
            />
          </div>
        </div>

        <div class="row items-start q-col-gutter-sm q-mb-sm">
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.externalId"
                :label="t('facebook.quick_conversion.fields.external_id')"
                dense
                filled
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.fbClickId"
                :label="t('facebook.quick_conversion.fields.facebook_click_id')"
                dense
                filled
                placeholder="fb.1.1234567890.abcdef"
            />
          </div>
          <div class="col-12 col-md-4">
            <q-input
                v-model="form.fields.eventUrl"
                :label="t('facebook.quick_conversion.fields.event_url')"
                dense
                filled
                placeholder="https://example.com/page"
            />
          </div>
        </div>

        <!-- Дополнительные поля -->
        <q-expansion-item
            :label="t('facebook.quick_conversion.additional_fields')"
            icon="expand_more"
            dense
        >
          <div class="q-pa-md">
            <div class="row items-start q-col-gutter-sm q-mb-sm">
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.city"
                    :label="t('facebook.quick_conversion.fields.city')"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.state"
                    :label="t('facebook.quick_conversion.fields.state')"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.country"
                    :label="t('facebook.quick_conversion.fields.country')"
                    dense
                    filled
                />
              </div>
            </div>

            <div class="row items-start q-col-gutter-sm q-mb-sm">
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.dob"
                    :label="t('facebook.quick_conversion.fields.birth_date')"
                    type="date"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.ip"
                    :label="t('facebook.quick_conversion.fields.ip_address')"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.payout"
                    :label="t('facebook.quick_conversion.fields.amount')"
                    type="number"
                    step="0.01"
                    dense
                    filled
                />
              </div>
            </div>

            <div class="row items-start q-col-gutter-sm q-mb-sm">
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.fbBrowserId"
                    :label="t('facebook.quick_conversion.fields.facebook_browser_id')"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.refererUrl"
                    :label="t('facebook.quick_conversion.fields.referer_url')"
                    dense
                    filled
                />
              </div>
              <div class="col-12 col-md-4">
                <q-input
                    v-model="form.fields.conversionTime"
                    :label="t('facebook.quick_conversion.fields.conversion_time')"
                    type="datetime-local"
                    dense
                    filled
                />
              </div>
            </div>

            <div class="row items-start q-col-gutter-sm q-mb-sm">
              <div class="col-12 col-md-8">
                <q-input
                    v-model="form.fields.userAgent"
                    :label="t('facebook.quick_conversion.fields.user_agent')"
                    dense
                    filled
                />
              </div>
            </div>
          </div>
        </q-expansion-item>

        <!-- Предварительная оценка качества -->
<!--        <div v-if="qualityPreview" class="q-mt-md">-->
<!--          <q-badge-->
<!--              :color="getQualityColor(qualityPreview)"-->
<!--              :label="t('facebook.quick_conversion.quality_preview', { score: qualityPreview })"-->
<!--          />-->
<!--        </div>-->

        <!-- Кнопки -->
        <div class="form-actions-bar q-mt-md">
          <q-btn
              type="submit"
              color="primary"
              :label="editMode ? t('common.save') : t('facebook.quick_conversion.send_conversion')"
              :loading="sending"
              :disable="!isFormValid"
              no-caps
          />
          <q-btn
              flat
              :label="t('facebook.quick_conversion.reset_form')"
              @click="resetForm"
              :disable="sending"
              no-caps
          />
        </div>
      </q-form>
    </q-card-section>

    <!-- Результат -->
    <q-card-section v-if="result.message" class="q-pt-none">
      <q-banner
          :class="result.success ? 'text-positive' : 'text-negative'"
      >
        <template v-slot:avatar>
          <q-icon :name="result.success ? 'check_circle' : 'error'" />
        </template>
        {{ result.message }}
        <div v-if="result.conversionId" class="text-caption q-mt-sm">
          <div>{{ t('facebook.quick_conversion.result.conversion_id') }}: {{ result.conversionId }}</div>
          <div v-if="result.qualityScore">{{ t('facebook.quick_conversion.result.data_quality') }}: {{ result.qualityScore }}/10</div>
          <div v-if="result.sourceUrl">{{ t('facebook.quick_conversion.result.url') }}: {{ result.sourceUrl }}</div>
        </div>
      </q-banner>
    </q-card-section>
  </q-card>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useQuasar } from 'quasar'
import { useI18n } from 'vue-i18n'
import api from '@/api/api'

const $q = useQuasar()
const { t } = useI18n()

const props = defineProps({
  autoFetchPixels: {
    type: Boolean,
    default: true
  },
  presetPixelId: {
    type: String,
    default: null
  },
  editMode: {
    type: Boolean,
    default: false
  },
  editConversion: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['conversion-sent'])

const form = ref({
  pixelId: '',
  eventName: 'Lead',
  fields: {
    fullName: '',
    phone: '',
    email: '',
    dob: '',
    city: '',
    state: '',
    country: '',
    ip: '',
    userAgent: '',
    payout: null,
    fbClickId: '',
    fbBrowserId: '',
    externalId: '',
    conversionTime: '',
    customEventName: '',
    eventUrl: '',
    refererUrl: ''
  }
})

const pixelOptions = ref([])
const eventOptions = ref([
  {
    name: 'Lead',
    key: 'lead'
  },
  {
    name: 'Purchase',
    key: 'purchase'
  },
  {
    name: 'CompleteRegistration',
    key: 'complete_registration'
  },
  {
    name: 'AddToCart',
    key: 'add_to_cart'
  },
  {
    name: 'ViewContent',
    key: 'view_content'
  },
  {
    name: 'Contact',
    key: 'contact'
  },
  {
    name: 'SubmitApplication',
    key: 'submit_application'
  },
  {
    name: 'Custom',
    key: 'custom'
  }
])

const loadingPixels = ref(false)
const loadingEvents = ref(false)
const sending = ref(false)

const result = ref({
  message: '',
  success: false,
  conversionId: null,
  qualityScore: null,
  sourceUrl: null
})

// Вычисляемые свойства
const isFormValid = computed(() => {
  return !!form.value.pixelId && !!form.value.eventName && (
      !!form.value.fields.email ||
      !!form.value.fields.phone ||
      !!form.value.fields.externalId ||
      !!form.value.fields.ip
  )
})

// Предварительная оценка качества
const qualityPreview = computed(() => {
  let score = 10

  // Проверяем наличие идентификаторов
  const identifiers = [
    form.value.fields.email,
    form.value.fields.phone,
    form.value.fields.fullName,
    form.value.fields.city,
    form.value.fields.state,
    form.value.fields.country,
    form.value.fields.externalId,
    form.value.fields.ip,
    form.value.fields.userAgent
  ].filter(Boolean)

  if (identifiers.length === 0) score -= 5
  else if (identifiers.length < 2) score -= 2

  // Проверяем URL
  if (!form.value.fields.eventUrl) score -= 1

  // Проверяем Facebook ID
  if (!form.value.fields.fbClickId && !form.value.fields.fbBrowserId) score -= 1

  return Math.max(0, score)
})

// Методы
const fetchPixels = async () => {
  loadingPixels.value = true
  try {
    const { data: response } = await api.get('facebook-accounts')

    // API возвращает объект с полем accounts, а не прямой массив
    const accounts = response.accounts || []

    if (!Array.isArray(accounts)) {
      throw new Error('API вернул некорректные данные: accounts не является массивом')
    }

    const allPixels = []
    for (const account of accounts) {
      try {
        const { data: pixelsResponse } = await api.get(`facebook-pixels?account_id=${account.id}`)

        // API может вернуть либо прямой массив, либо объект с полем pixels
        let pixelsList
        if (Array.isArray(pixelsResponse)) {
          // Прямой массив пикселей
          pixelsList = pixelsResponse
        } else if (pixelsResponse && Array.isArray(pixelsResponse.pixels)) {
          // Объект с полем pixels
          pixelsList = pixelsResponse.pixels
        } else {
          // Неожиданный формат, пропускаем
          console.warn(`Неожиданный формат ответа пикселей для аккаунта ${account.id}:`, pixelsResponse)
          continue
        }

        allPixels.push(...pixelsList.map(pixel => ({
          pixel_id: pixel.pixel_id,
          label: `${pixel.pixel_name || pixel.pixel_id} (${pixel.pixel_id}) - ${account.account_name}`,
          account_name: account.account_name
        })))
      } catch (err) {
        console.error(`Ошибка загрузки пикселей для аккаунта ${account.id}:`, err)
      }
    }
    pixelOptions.value = allPixels.filter(pixel => pixel.pixel_id)

    if (allPixels.length === 0) {
      $q.notify({
        type: 'info',
        message: t('facebook.quick_conversion.notifications.pixels_not_found'),
        timeout: 5000
      })
    }
  } catch (err) {
    console.error('Ошибка загрузки пикселей:', err)
    $q.notify({
      type: 'negative',
      message: t('facebook.quick_conversion.notifications.pixels_load_error') + ': ' + (err.message || 'Неизвестная ошибка')
    })
  } finally {
    loadingPixels.value = false
  }
}

const sendConversion = async () => {
  if (!isFormValid.value) {
    $q.notify({
      type: 'warning',
      message: t('facebook.quick_conversion.notifications.fill_required_fields')
    })
    return
  }

  sending.value = true
  result.value = { message: '', success: false, conversionId: null }

  try {
    // Подготавливаем данные пользователя
    const userData = {}
    if (form.value.fields.email) userData.email = form.value.fields.email
    if (form.value.fields.phone) userData.phone = form.value.fields.phone
    if (form.value.fields.fullName) {
      const names = form.value.fields.fullName.split(' ')
      userData.first_name = names[0]
      if (names.length > 1) {
        userData.last_name = names.slice(1).join(' ')
      }
    }
    if (form.value.fields.city) userData.city = form.value.fields.city
    if (form.value.fields.state) userData.state = form.value.fields.state
    if (form.value.fields.country) userData.country = form.value.fields.country
    if (form.value.fields.dob) userData.date_of_birth = form.value.fields.dob
    if (form.value.fields.ip) userData.client_ip_address = form.value.fields.ip
    if (form.value.fields.userAgent) userData.client_user_agent = form.value.fields.userAgent
    if (form.value.fields.fbClickId) userData.fbc = form.value.fields.fbClickId
    if (form.value.fields.fbBrowserId) userData.fbp = form.value.fields.fbBrowserId
    if (form.value.fields.externalId) userData.external_id = form.value.fields.externalId

    // Подготавливаем custom data
    const customData = {}
    if (form.value.fields.payout) {
      customData.value = form.value.fields.payout
      customData.currency = 'USD'
    }

    let response

    if (props.editMode && props.editConversion) {
      // РЕЖИМ РЕДАКТИРОВАНИЯ - отправляем запрос на редактирование
      response = await api.post('api/facebook-conversions', {
        action: 'edit_conversion',
        conversion_id: props.editConversion.id,
        additional_data: {
          user_data: userData,
          custom_data: customData,
          url: form.value.fields.eventUrl
        }
      })
    } else {
      // РЕЖИМ СОЗДАНИЯ - отправляем новую конверсию
      const conversionData = {
        pixel_id: form.value.pixelId,
        event_name: form.value.eventName === 'Custom' ?
            form.value.fields.customEventName : form.value.eventName,
        user_data: userData,
        custom_data: customData,
        action_source: 'website'
      }

      if (form.value.fields.eventUrl) {
        conversionData.event_source_url = form.value.fields.eventUrl
      }

      if (form.value.fields.conversionTime) {
        conversionData.event_time = Math.floor(new Date(form.value.fields.conversionTime).getTime() / 1000)
      }

      response = await api.post('facebook-conversions', conversionData)
    }

    const data = response.data

    result.value = {
      message: props.editMode ?
          t('facebook.quick_conversion.notifications.conversion_update_success') :
          t('facebook.quick_conversion.notifications.conversion_sent_success'),
      success: true,
      conversionId: data.conversion_id || props.editConversion?.id,
      qualityScore: data.quality_score,
      sourceUrl: form.value.fields.eventUrl
    }

    // Очищаем форму только при создании новой конверсии
    if (!props.editMode) {
      resetForm()
    }

    // Уведомляем родительский компонент
    emit('conversion-sent', data)

    $q.notify({
      type: 'positive',
      message: props.editMode ?
          t('facebook.quick_conversion.notifications.conversion_update_success') :
          t('facebook.quick_conversion.notifications.conversion_sent_success'),
      caption: data.facebook_trace_id ? `Trace ID: ${data.facebook_trace_id}` : ''
    })

  } catch (error) {
    console.error('Ошибка:', error)

    result.value = {
      message: error.response?.data?.error || error.message ||
          (props.editMode ? t('facebook.quick_conversion.notifications.conversion_update_error') : t('facebook.quick_conversion.notifications.conversion_send_error')),
      success: false,
      conversionId: null
    }

    $q.notify({
      type: 'negative',
      message: props.editMode ? t('facebook.quick_conversion.notifications.conversion_update_error') : t('facebook.quick_conversion.notifications.conversion_send_error'),
      caption: error.response?.data?.error || error.message
    })
  } finally {
    sending.value = false
  }
}

const resetForm = () => {
  form.value.fields = {
    fullName: '',
    phone: '',
    email: '',
    dob: '',
    city: '',
    state: '',
    country: '',
    ip: '',
    userAgent: '',
    payout: null,
    fbClickId: '',
    fbBrowserId: '',
    externalId: '',
    conversionTime: '',
    customEventName: '',
    eventUrl: '',
    refererUrl: ''
  }
  result.value = { message: '', success: false, conversionId: null }
}

// Метод для заполнения формы при редактировании
const fillFormFromConversion = (conversion) => {
  if (!conversion) return

  // Парсим данные
  const userData = typeof conversion.user_data === 'string'
      ? JSON.parse(conversion.user_data || '{}')
      : (conversion.user_data || {})

  const customData = typeof conversion.custom_data === 'string'
      ? JSON.parse(conversion.custom_data || '{}')
      : (conversion.custom_data || {})

  // Заполняем форму
  form.value.pixelId = conversion.pixel_id
  form.value.eventName = conversion.event_name

  // Объединяем имя и фамилию
  let fullName = ''
  if (userData.first_name) fullName += userData.first_name
  if (userData.last_name) fullName += (fullName ? ' ' : '') + userData.last_name

  form.value.fields = {
    fullName: fullName,
    email: userData.email || '',
    phone: userData.phone || '',
    dob: userData.date_of_birth || '',
    city: userData.city || '',
    state: userData.state || '',
    country: userData.country || '',
    ip: userData.client_ip_address || '',
    userAgent: userData.client_user_agent || '',
    payout: customData.value || null,
    fbClickId: userData.fbc || '',
    fbBrowserId: userData.fbp || '',
    externalId: userData.external_id || '',
    conversionTime: '',
    customEventName: '',
    eventUrl: conversion.source_url || '',
    refererUrl: ''
  }
}

// Watcher для editConversion
watch(() => props.editConversion, (newConversion) => {
  if (newConversion && props.editMode) {
    fillFormFromConversion(newConversion)
  }
}, { immediate: true })

const getQualityColor = (score) => {
  if (score >= 8) return 'positive'
  if (score >= 6) return 'warning'
  if (score >= 4) return 'orange'
  return 'negative'
}

// Автозаполнение некоторых полей
const autoFillTechnicalFields = () => {
  // Автозаполнение User Agent
  if (!form.value.fields.userAgent) {
    form.value.fields.userAgent = navigator.userAgent
  }
}

onMounted(() => {
  if (props.autoFetchPixels) {
    fetchPixels()
  }
  if (props.presetPixelId) {
    form.value.pixelId = props.presetPixelId
  }
  autoFillTechnicalFields()
})

// Watchers
watch(() => form.value.fields.eventUrl, (newUrl) => {
  // Убираем назойливое уведомление - пользователь и так это знает
})

// Экспортируем методы и данные для внешнего использования
defineExpose({
  resetForm,
  sendConversion,
  fetchPixels,
  form: form.value,
  isFormValid,
  qualityPreview
})
</script>

<style scoped>
.form-compact .q-field {
  margin-bottom: 8px;
}

.select-popup-compact .q-item {
  min-height: 40px;
}

.select-label-compact {
  font-size: 14px;
  line-height: 1.2;
}

.select-caption-compact {
  font-size: 11px;
  opacity: 0.7;
}

.form-actions-bar {
  border-top: 1px solid #e0e0e0;
  padding-top: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.q-banner {
  border-radius: 8px;
}

.mb-0 {
  margin-bottom: 0 !important;
}
</style>